import { useEffect, useState, useMemo, useCallback } from 'react';
import { List, X } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TocProps {
  content: string;
  className?: string;
}

/**
 * Extract headings from markdown content
 */
function extractHeadings(content: string): TocItem[] {
  const headings: TocItem[] = [];
  const headingRegex = /^(#{1,6})\s+(.+)$/gm;
  let match;

  while ((match = headingRegex.exec(content)) !== null) {
    const level = match[1].length;
    const text = match[2].trim();
    // Generate ID from text: lowercase, replace spaces with dashes, remove special chars
    const id = text
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^\w\u4e00-\u9fa5-]/g, '');

    headings.push({ id, text, level });
  }

  return headings;
}

export function Toc({ content, className = '' }: TocProps) {
  const [activeId, setActiveId] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  const headings = useMemo(() => extractHeadings(content), [content]);

  // Generate anchor IDs in the DOM
  useEffect(() => {
    const contentEl = document.querySelector('.markdown-content');
    if (!contentEl) return;

    // Find all headings and add IDs
    const headingElements = contentEl.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headingElements.forEach((el) => {
      const text = el.textContent?.trim() || '';
      const id = text
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\u4e00-\u9fa5-]/g, '');
      el.id = id;

      // Add anchor link
      const anchor = document.createElement('a');
      anchor.className = 'anchor-link';
      anchor.href = `#${id}`;
      anchor.innerHTML = '#';
      anchor.style.cssText = `
        margin-left: 8px;
        color: var(--muted-foreground);
        text-decoration: none;
        opacity: 0;
        transition: opacity 0.2s;
      `;
      el.appendChild(anchor);

      // Show anchor on hover
      el.addEventListener('mouseenter', () => {
        anchor.style.opacity = '1';
      });
      el.addEventListener('mouseleave', () => {
        anchor.style.opacity = '0';
      });
    });

    return () => {
      // Cleanup: remove anchor links
      headingElements.forEach((el) => {
        const anchor = el.querySelector('.anchor-link');
        if (anchor) anchor.remove();
      });
    };
  }, [content]);

  // Intersection Observer for active heading
  useEffect(() => {
    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first heading that is visible
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          // Sort by position, get the topmost one
          const topmost = visibleEntries.reduce((prev, current) => {
            return prev.boundingClientRect.top < current.boundingClientRect.top
              ? prev
              : current;
          });
          setActiveId(topmost.target.id);
        }
      },
      {
        rootMargin: '-80px 0px -70% 0px',
        threshold: 0,
      }
    );

    const contentEl = document.querySelector('.markdown-content');
    if (!contentEl) return;

    const headingElements = contentEl.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headingElements.forEach((el) => observer.observe(el));

    return () => {
      headingElements.forEach((el) => observer.unobserve(el));
    };
  }, [headings]);

  // Handle click to scroll
  const scrollToHeading = useCallback((id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80; // Header height
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
      // Update URL hash
      window.history.pushState(null, '', `#${id}`);
      setActiveId(id);
      setIsOpen(false);
    }
  }, []);

  // Handle initial hash on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        scrollToHeading(hash);
      }, 100);
    }
  }, [scrollToHeading]);

  if (headings.length === 0) {
    return null;
  }

  // Calculate min level to normalize indentation
  const minLevel = Math.min(...headings.map((h) => h.level));

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-50 lg:hidden bg-primary text-primary-foreground p-3 rounded-full shadow-lg hover:opacity-90 transition-opacity"
        aria-label="Toggle table of contents"
      >
        {isOpen ? <X className="h-5 w-5" /> : <List className="h-5 w-5" />}
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* TOC panel */}
      <div
        className={`
          fixed bottom-20 right-4 z-40 w-72 max-h-[60vh] overflow-y-auto
          bg-background border rounded-lg shadow-lg p-4
          transition-all duration-200
          lg:top-20 lg:bottom-auto lg:right-auto lg:w-full lg:max-h-[calc(100vh-100px)]
          lg:shadow-none lg:border-0 lg:bg-transparent
          ${isOpen ? 'block' : 'hidden lg:block'}
          ${className}
        `}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wider">
            目录
          </h3>
          <button
            onClick={() => setIsOpen(false)}
            className="lg:hidden text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="space-y-1">
          {headings.map((heading) => {
            const indent = heading.level - minLevel;
            const isActive = activeId === heading.id;

            return (
              <button
                key={heading.id}
                onClick={() => scrollToHeading(heading.id)}
                className={`
                  w-full text-left text-sm py-1.5 px-2 rounded
                  transition-colors duration-200
                  hover:bg-muted
                  ${isActive
                    ? 'text-primary font-medium bg-muted/50'
                    : 'text-muted-foreground'
                  }
                `}
                style={{ paddingLeft: `${indent * 12 + 8}px` }}
              >
                {heading.text}
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}
